import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, map, Observable, takeWhile} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {ConfirmationService} from 'primeng/api';
import {PropertyDetails} from '../../models/property-details';
import {RouteContextService} from '../../../context/services/route-context.service';
import * as propertyPictureActions from '../../actions/property-picture';
import {IContext} from '../../../context/models/context';
import {IGalleryItem} from '../../../common/models/gallery-item';
import {WidgetComponent} from '../../../dashboards/components/widget-component/widget-component';
import {UserPermissionsService} from '../../../permissions/user-permissions';
import {ActionResult} from '../../models/action-result';

@Component({
  selector: 'mojo-property-picture',
  templateUrl: './property-picture.component.html'
})
export class PropertyPictureComponent extends WidgetComponent implements OnInit, OnDestroy {
  @Input() propertyDetails: PropertyDetails;

  supportedFileTypes: string[] = ['jpeg', 'jpg', 'png', 'jfif'];
  activeIndex: number = 0;
  displayFileUploadWigetSubject = new BehaviorSubject<boolean>(false);
  displayFileUploadWidget = this.displayFileUploadWigetSubject.asObservable();
  images: IGalleryItem[];
  galleryItems: Observable<IGalleryItem[]>;
  context: IContext;
  showThumbnails: boolean = false;
  alive: boolean = true;
  isMaximised$: Observable<boolean>

  constructor(
    protected store: Store<any>,
    private readonly contextService: RouteContextService,
    private readonly activatedRoute: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private permissionService: UserPermissionsService,
    protected confirmationService: ConfirmationService,
    private toasterService: ToastrService) {

    super(store, confirmationService);
    this.context = this.contextService.getContextFromRoute(this.activatedRoute);
    this.store.dispatch(new propertyPictureActions.Load(this.context.PropertyRef));
    this.galleryItems = this.store.pipe(select((state) => state.propertyPictures.propertyImages));
    this.images = [];

    this.store
      .pipe(
        select(state => state.propertyPictures.actionResult),
        takeWhile(() => this.alive)
      ).subscribe((result: ActionResult) => {
      if (result) {
        if (result.success) {
          this.toasterService.success(result.message);
        } else {
          this.toasterService.error(result.message);
        }
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();

    this.isMaximised$ = this.maximisedWidget$.pipe(
      map(maximisedWidget => maximisedWidget === this.widget)
    );

    this.galleryItems.pipe(
      takeWhile(() => this.alive)
    ).subscribe(imagesFromStore => {
      this.images = [];
      if (imagesFromStore !== undefined) {
        imagesFromStore.forEach(image => {
          this.images.push(image);
        });
      }
      this.displayFileUploadWigetSubject.next(this.images.length === 0);
    });
  }

  ngOnDestroy() {
    this.alive = false;
  }

  fileUploadEvent(event) {
    this.store.dispatch(new propertyPictureActions.Upload(this.context, event.files));
  }

  fileSizeLimitedExceeded(message: string) {
    this.toasterService.error(message);
    this.displayFileUploadWigetSubject.next(false);
  }

  fileTypeNotSupported(message: string) {
    this.toasterService.error(message);
    this.displayFileUploadWigetSubject.next(false);
  }

  mouseEnterEvent() {
    this.showThumbnails = true;
    this.changeDetector.detectChanges();
  }

  mouseLeaveEvent() {
    this.showThumbnails = false;
    this.changeDetector.detectChanges();
  }

  dragoverEvent(event) {
    event.preventDefault();
    this.displayFileUploadWigetSubject.next(true);
  }

  dragleaveEvent(event) {
    event.preventDefault();
    if (this.images.length > 0)
      this.displayFileUploadWigetSubject.next(false);
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  deleteImage(index: number) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this image?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const image = this.images[index];
        this.store.dispatch(new propertyPictureActions.Delete(image.DocumentKey, this.context.PropertyRef, image.Filename));

        if (this.images.length > 1) {
          this.activeIndex = this.activeIndex - 1;
        }
      }
    });

  }

  userCanDelete(): Observable<boolean> {
    return this.permissionService.canDeletePropertyPictures();
  }

  userCanUpload(): boolean {
    return this.permissionService.canUploadPropertyPictures();
  }

  onSelectEvent(event) {
    if (this.images.length > 0) {
      this.displayFileUploadWigetSubject.next(false);
    }
  }
}


